import { logger } from './logger';
import { ConfigError } from './types';

/**
 * Generate a UUID v4
 */
export function generateUUID(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * Sleep utility
 */
export function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Check if running in Chrome extension context
 */
export function isExtensionContext(): boolean {
  return (
    typeof chrome !== 'undefined' && !!chrome.runtime && !!chrome.runtime.id
  );
}

/**
 * Safe JSON parse with fallback
 */
export function safeJsonParse<T>(json: string, fallback: T): T {
  try {
    return JSON.parse(json);
  } catch {
    return fallback;
  }
}

/**
 * Detects if the code is executing in a content script context.
 *
 * @remarks
 * - Uses `window.location.protocol !== 'chrome-extension:'` to infer content scripts
 *   (content scripts run on http/https/file pages, not chrome-extension pages).
 * - Returns `false` when `window` is unavailable (e.g., background/service worker),
 *   so background and offscreen documents will be treated as extension contexts.
 */
export function isContentScript(): boolean {
  if (typeof window === 'undefined') return false;
  // Content scripts run on http/https/file pages
  // Extension pages (popup, sidepanel) run on chrome-extension:
  return window.location.protocol !== 'chrome-extension:';
}

/**
 * Validate that the requested placement matches the actual execution context.
 *
 * @param placement Placement identifier to verify against the current context
 *
 * @throws {ConfigError} When the placement does not match the detected context.
 *
 * @notes
 * - Content scripts MUST use placement `'content_script'`. Any other placement
 *   from a content script is rejected to prevent developers from misreporting
 *   where ads are displayed.
 * - Extension pages (popup, sidepanel, internal) are validated against the current
 *   URL when `window` is available.
 * - Background/service worker code will never throw because `window` is absent.
 */
export function validatePlacementContext(placement?: string): void {
  // Content scripts must use the 'content_script' placement
  if (isContentScript()) {
    if (placement && placement !== 'content_script') {
      throw new ConfigError(
        `Placement mismatch: '${placement}' requested but running in a content script context. Use 'content_script' placement for content scripts.`
      );
    }
    return;
  }

  // Extension page URL-based verification
  if (typeof window !== 'undefined' && placement) {
    const url = window.location.href.toLowerCase();

    if (placement === 'popup' && !url.includes('popup')) {
      logger.warn(
        `Placement mismatch: 'popup' requested but running on ${window.location.pathname}`
      );
    }

    if (placement === 'sidepanel' && !url.includes('sidepanel')) {
      logger.warn(
        `Placement mismatch: 'sidepanel' requested but running on ${window.location.pathname}`
      );
    }
  }
}

/**
 * Constants
 */
export const DEFAULT_BASE_URL = 'https://www.playayield.com';
export const AD_REQUEST_ENDPOINT = '/api/ads';
export const TRACK_IMPRESSION_ENDPOINT = '/api/ads/track-impression';
export const REQUEST_TIMEOUT = 5000;
